'use client';
import { useCallback } from 'react';

import { useMixpanel } from '@/hooks/useMixpanel';
import { useOpenAuthModal } from '@/hooks/useOpenAuthModal';
import { useToggleBookmark } from '@/services/query/bookmark.queries';
import { useAppSelector, useAuth } from '@/store';
import type { EventResponse, Market } from '@/types';

export const useEventBookmarkAction = () => {
  const { trackMarketBookmarked } = useMixpanel();
  const { isAuthenticated } = useAuth();
  const { mutate: toggleBookmark } = useToggleBookmark();

  const openAuthModal = useOpenAuthModal();

  const bookmarkedEventIds = useAppSelector((s) => s.bookmarks.bookmarkedEventIds);

  const checkIsBookmarked = useCallback(
    (id: string) => {
      return bookmarkedEventIds.includes(id);
    },
    [bookmarkedEventIds]
  );

  const handleBookmarkToggle = useCallback(
    (
      e: React.MouseEvent | undefined,
      item: Market | EventResponse | null,
      _type: 'market' | 'sport',
      _customUrl?: string
    ) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (!isAuthenticated) {
        openAuthModal();
        return;
      }

      if (!item) return;

      // Fallback to id if eventId is not present. Both Market and EventResponse have id.
      const eventId = (item as any).eventId || item.id;

      if (eventId) {
        const isCurrentlyBookmarked = bookmarkedEventIds.includes(eventId);
        if (!isCurrentlyBookmarked) {
          trackMarketBookmarked({
            market_id: eventId,
            market_title: (item as any).title || (item as any).question || item.description || '',
            category: (item as any).category || (item as any).groupItemTitle || '',
          });
        }
        toggleBookmark(eventId);
      }
    },
    [isAuthenticated, toggleBookmark, openAuthModal, bookmarkedEventIds, trackMarketBookmarked]
  );

  return { checkIsBookmarked, handleBookmarkToggle };
};
